import React, { useState, useEffect } from 'react';
import { 
  Plus, X, Upload, Sparkles, Check, Image as ImageIcon, 
  Loader2, Layers, CheckCircle2, Search, Zap, AlertCircle, ChevronDown 
} from 'lucide-react';
import { useCollection } from '../context/CollectionContext';
import { useLanguage } from '../context/LanguageContext';
import { soundEffects } from '../services/audio';
import { uploadCardImageToStorage, downloadAndUploadImageToStorage } from '../services/firebase';
import { DeckValidatorTab } from './DeckValidatorTab';
import { lookupCardOnline, CardLookupResult } from '../services/cardLookup';
import { readFileSmart } from '../utils/textSanitizer';

interface AddCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'manual' | 'csv' | 'validate';
  initialName?: string;
}

export const AddCardModal: React.FC<AddCardModalProps> = ({ isOpen, onClose, initialTab, initialName }) => {
  const { addNewCard, importCardsFromCsv, decks } = useCollection();
  const { t } = useLanguage();
  const [tab, setTab] = useState<'manual' | 'csv' | 'validate'>(initialTab || 'manual');

  useEffect(() => {
    if (initialTab && isOpen) {
      setTab(initialTab);
    }
  }, [initialTab, isOpen]);

  useEffect(() => {
    if (isOpen && initialName) {
      setNameEn(initialName);
      setNamePt(initialName);
      setQuickQuery(initialName);
    }
  }, [isOpen, initialName]);

  // Manual Form State
  const [nameEn, setNameEn] = useState('');
  const [namePt, setNamePt] = useState('');
  const [setCode, setSetCode] = useState('SV1');
  const [setEn, setSetEn] = useState('Scarlet & Violet');
  const [cardNumber, setCardNumber] = useState('');
  const [totalInSet, setTotalInSet] = useState('198');
  const [colorCode, setColorCode] = useState('R');
  const [rarityCode, setRarityCode] = useState('C');
  const [quantity, setQuantity] = useState(1);
  const [quality, setQuality] = useState('NM');
  const [isFoil, setIsFoil] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [comment, setComment] = useState('');

  // CSV Import State
  const [csvText, setCsvText] = useState('');
  const [deckMode, setDeckMode] = useState<'none' | 'existing' | 'new'>('none');
  const [targetDeckId, setTargetDeckId] = useState<string>(decks[0]?.id || '');
  const [newDeckName, setNewDeckName] = useState<string>('');
  const [newDeckFormat, setNewDeckFormat] = useState<'Standard' | 'Expanded' | 'Casual'>('Standard');
  const [importResult, setImportResult] = useState<{ added: number; updated: number; deckName?: string } | null>(null);

  // Smart Autofill Search State
  const [quickQuery, setQuickQuery] = useState('');
  const [isSearchingCard, setIsSearchingCard] = useState(false);
  const [searchCandidates, setSearchCandidates] = useState<CardLookupResult[]>([]);
  const [searchError, setSearchError] = useState('');
  const [searchSuccess, setSearchSuccess] = useState('');
  const [addedSuccessInfo, setAddedSuccessInfo] = useState<{
    name: string;
    setCode: string;
    cardNumber: string;
    imageUrl?: string;
    isDuplicate?: boolean;
    quantity?: number;
  } | null>(null);

  const clearForm = () => {
    setNamePt('');
    setNameEn('');
    setSetCode('SV1');
    setSetEn('Scarlet & Violet');
    setCardNumber('');
    setTotalInSet('198');
    setColorCode('R');
    setRarityCode('C');
    setQuantity(1);
    setQuality('NM');
    setIsFoil(false);
    setImageUrl('');
    setImageFile(null);
    setImagePreview(null);
    setQuickQuery('');
    setSearchCandidates([]);
    setSearchError('');
    setSearchSuccess('');
    setComment('');
  };

  const handleResetFormForAnother = () => {
    soundEffects.playClick();
    clearForm();
    setAddedSuccessInfo(null);
  };

  const handleCloseModal = () => {
    soundEffects.playClick();
    clearForm();
    setAddedSuccessInfo(null);
    onClose();
  };

  const handleAddCandidateDirectly = (c: CardLookupResult, e: React.MouseEvent) => {
    e.stopPropagation();
    soundEffects.playScan();
    const cleanSet = (c.setCode || 'SV1').trim().toUpperCase();
    const cleanNum = (c.cardNumber || '1').trim();
    const fallbackImage = `https://images.pokemontcg.io/${cleanSet.toLowerCase()}/${cleanNum.replace(/\D/g, '') || '1'}.png`;
    const finalImage = c.imageUrl || fallbackImage;

    const normalizedColor = c.colorCode === 'N' ? 'O' : c.colorCode === 'T' ? '' : (c.colorCode ?? 'C');

    const added = addNewCard({
      name_en: c.nameEn || c.namePt,
      name_pt: c.namePt || c.nameEn,
      set_code: cleanSet,
      set_en: c.setName,
      set_pt: c.setName,
      card_number: cleanNum,
      total_in_set: c.totalInSet || '100',
      color_code: normalizedColor,
      card_category: c.category,
      rarity_code: c.rarityCode || 'C',
      quantity: 1,
      quality: 'NM',
      is_foil: false,
      image_url: finalImage,
      comment: ''
    });

    clearForm();
    soundEffects.playSuccess();
    setAddedSuccessInfo({
      name: added.name_pt || added.name_en,
      setCode: added.set_code,
      cardNumber: added.card_number,
      imageUrl: finalImage,
      isDuplicate: added.isDuplicate,
      quantity: added.quantity
    });
  };

  const handleQuickSearch = async () => {
    if (!quickQuery.trim()) return;
    soundEffects.playClick();
    setIsSearchingCard(true);
    setSearchError('');
    setSearchSuccess('');
    setSearchCandidates([]);

    try {
      const results = await lookupCardOnline(quickQuery);
      if (results.length === 0) {
        setSearchError(t('addCard.quickSearchNotFound', { query: quickQuery }));
      } else if (results.length === 1) {
        applyCardCandidate(results[0]);
      } else {
        setSearchCandidates(results);
      }
    } catch (err) {
      console.error('Error in quick card search:', err);
      setSearchError('Erro ao buscar dados da carta. Tente novamente.');
    } finally {
      setIsSearchingCard(false);
    }
  };

  const applyCardCandidate = (candidate: CardLookupResult) => {
    soundEffects.playScan();
    setNamePt(candidate.namePt);
    setNameEn(candidate.nameEn);
    setSetCode(candidate.setCode);
    setSetEn(candidate.setName);
    setCardNumber(candidate.cardNumber);
    setTotalInSet(candidate.totalInSet);
    const normalizedCandidateColor = candidate.colorCode === 'N' ? 'O' : candidate.colorCode === 'T' ? '' : candidate.colorCode;
    setColorCode(normalizedCandidateColor);
    setRarityCode(candidate.rarityCode);
    setImageUrl(candidate.imageUrl || '');
    setImagePreview(candidate.imageUrl || null);
    setSearchCandidates([]);
    setSearchSuccess(t('addCard.quickSearchSuccess', {
      name: candidate.namePt || candidate.nameEn,
      set: candidate.setCode,
      num: candidate.cardNumber
    }));
    setTimeout(() => setSearchSuccess(''), 4500);
  };

  if (!isOpen) return null;

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameEn.trim() && !namePt.trim()) return;

    soundEffects.playScan();
    setIsUploadingImage(true);

    const cleanSet = setCode.trim().toLowerCase();
    const cleanNum = cardNumber.trim().replace(/\D/g, '') || '1';
    const targetFilename = `${cleanSet}_${cleanNum}.png`;

    let finalImageUrl = imageUrl.trim();

    try {
      if (imageFile) {
        finalImageUrl = await uploadCardImageToStorage(imageFile, targetFilename);
      } else if (finalImageUrl && !finalImageUrl.includes('firebasestorage.googleapis.com')) {
        finalImageUrl = await downloadAndUploadImageToStorage(finalImageUrl, targetFilename);
      } else if (!finalImageUrl) {
        finalImageUrl = `https://images.pokemontcg.io/${cleanSet}/${cleanNum}.png`;
      }
    } catch (err) {
      console.warn('Storage upload warning, using fallback URL:', err);
    } finally {
      setIsUploadingImage(false);
    }

    const added = addNewCard({
      name_en: nameEn.trim() || namePt.trim(),
      name_pt: namePt.trim() || nameEn.trim(),
      set_code: setCode.trim().toUpperCase(),
      set_en: setEn.trim(),
      set_pt: setEn.trim(),
      card_number: cardNumber.trim() || '1',
      total_in_set: totalInSet.trim() || '100',
      color_code: colorCode,
      card_category: colorCode === '' ? 'Trainer' : colorCode === 'E' ? 'Energy' : 'Pokémon',
      rarity_code: rarityCode,
      quantity: Number(quantity) || 1,
      quality,
      is_foil: isFoil,
      image_url: finalImageUrl,
      comment: comment.trim()
    });

    clearForm();
    soundEffects.playSuccess();
    setAddedSuccessInfo({
      name: added.name_pt || added.name_en,
      setCode: added.set_code,
      cardNumber: added.card_number,
      imageUrl: finalImageUrl,
      isDuplicate: added.isDuplicate,
      quantity: added.quantity
    });
  };

  const handleCsvImport = () => {
    if (!csvText.trim()) return;
    const res = importCardsFromCsv(csvText, {
      mode: deckMode,
      existingDeckId: deckMode === 'existing' ? targetDeckId : undefined,
      newDeckName: deckMode === 'new' ? newDeckName : undefined,
      newDeckFormat: deckMode === 'new' ? newDeckFormat : undefined,
    });
    setImportResult(res);
    setTimeout(() => {
      setImportResult(null);
      handleCloseModal();
    }, 2500);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!newDeckName) {
        const cleanName = file.name
          .replace(/\.[^/.]+$/, '')
          .replace(/^export_/, 'Deck ')
          .substring(0, 30);
        setNewDeckName(cleanName);
      }
      readFileSmart(file).then(text => {
        if (text) setCsvText(text);
      }).catch(err => {
        console.error('Error reading CSV file:', err);
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pb-20 sm:pb-4 bg-black/85 backdrop-blur-md overflow-y-auto animate-in fade-in">
      <div className="relative w-full max-w-xl bg-pokedex-screen border-4 border-pokedex-darkred rounded-3xl shadow-2xl overflow-hidden text-slate-100 my-8">
        {/* Top Header */}
        <div className="bg-pokedex-red px-6 py-3 border-b-2 border-pokedex-darkred flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Plus className="w-5 h-5 text-yellow-300" />
            <span className="font-display font-bold text-sm text-white uppercase tracking-wider">
              {t('addCard.title')}
            </span>
          </div>

          <button
            onClick={handleCloseModal}
            aria-label={t('common.close')}
            className="w-8 h-8 rounded-full bg-pokedex-darkred hover:bg-black/40 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-slate-800 bg-pokedex-darker text-xs font-mono">
          <button
            onClick={() => { soundEffects.playClick(); setTab('manual'); }}
            className={`flex-1 py-3 px-2 font-bold transition-colors text-center truncate ${
              tab === 'manual' ? 'bg-slate-800 text-yellow-300 border-b-2 border-yellow-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            {t('addCard.tabManual')}
          </button>
          <button
            onClick={() => { soundEffects.playClick(); setTab('csv'); }}
            className={`flex-1 py-3 px-2 font-bold transition-colors text-center truncate ${
              tab === 'csv' ? 'bg-slate-800 text-yellow-300 border-b-2 border-yellow-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            {t('addCard.tabCsv')}
          </button>
          <button
            onClick={() => { soundEffects.playClick(); setTab('validate'); }}
            className={`flex-1 py-3 px-2 font-bold transition-colors flex items-center justify-center space-x-1.5 truncate ${
              tab === 'validate' ? 'bg-slate-800 text-yellow-300 border-b-2 border-yellow-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
            <span className="truncate">{t('addCard.tabValidate')}</span>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-5 sm:p-6 max-h-[75vh] overflow-y-auto font-mono text-xs">
          {tab === 'manual' ? (
            addedSuccessInfo ? (
              <div className="py-8 px-4 flex flex-col items-center text-center space-y-4 animate-in zoom-in-95 duration-200">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center text-emerald-400 shadow-[0_0_30px_rgba(16,185,129,0.35)] animate-bounce">
                  <CheckCircle2 className="w-10 h-10" />
                </div>

                {addedSuccessInfo.imageUrl && (
                  <div className="w-24 h-32 rounded-xl overflow-hidden border-2 border-emerald-400/50 shadow-2xl my-1 bg-black/60">
                    <img src={addedSuccessInfo.imageUrl} alt="" className="w-full h-full object-cover" />
                  </div>
                )}

                <div className="space-y-1">
                  <h3 className="text-base font-black text-emerald-400 uppercase tracking-wider font-mono">
                    {addedSuccessInfo.isDuplicate
                      ? t('addCard.cardUpdatedSuccessTitle')
                      : t('addCard.cardAddedSuccessTitle')}
                  </h3>
                  <p className="text-xs text-white font-mono font-bold flex items-center justify-center gap-1.5 flex-wrap">
                    <span>{addedSuccessInfo.name}</span>
                    <span className="text-slate-400">({addedSuccessInfo.setCode} #{addedSuccessInfo.cardNumber})</span>
                    {addedSuccessInfo.quantity !== undefined && (
                      <span className="px-2 py-0.5 rounded-full bg-yellow-400/20 text-yellow-300 border border-yellow-400/40 text-[10px] font-mono">
                        x{addedSuccessInfo.quantity}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-400 max-w-sm mx-auto font-sans">
                    {addedSuccessInfo.isDuplicate
                      ? t('addCard.cardUpdatedSuccessDesc', { count: addedSuccessInfo.quantity ?? 1 })
                      : t('addCard.cardAddedSuccessDesc')}
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleResetFormForAnother}
                    className="bg-yellow-400 hover:bg-yellow-300 text-slate-950 font-black px-4 py-2.5 rounded-xl text-xs flex items-center gap-2 shadow active:scale-95 transition-all font-mono"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{t('addCard.addAnotherCard')}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      soundEffects.playClick();
                      setAddedSuccessInfo(null);
                      onClose();
                    }}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold px-4 py-2.5 rounded-xl text-xs transition-colors border border-slate-700 font-mono"
                  >
                    <span>{t('addCard.finishBtn')}</span>
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleManualSubmit} className="space-y-4">
              {/* Smart Autofill Box */}
              <div className="bg-gradient-to-r from-slate-900 to-slate-950 p-3.5 rounded-2xl border-2 border-yellow-400/50 shadow-xl space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-yellow-300 font-bold text-[11px] uppercase tracking-wider flex items-center gap-1.5 font-mono">
                    <Zap className="w-3.5 h-3.5 text-yellow-400" />
                    <span>{t('addCard.quickSearchLabel')}</span>
                  </label>
                  <span className="text-[9px] text-slate-400 font-mono">TCGdex & Pokédex API</span>
                </div>

                <div className="space-y-2">
                  <div className="relative w-full">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      type="text"
                      value={quickQuery}
                      onChange={(e) => setQuickQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleQuickSearch();
                        }
                      }}
                      placeholder={t('addCard.quickSearchPlaceholder')}
                      className="w-full bg-black/60 border border-slate-700 rounded-xl pl-9 pr-3 py-2.5 text-white focus:outline-none focus:border-yellow-400 text-xs font-sans placeholder-slate-500"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleQuickSearch}
                    disabled={!quickQuery.trim() || isSearchingCard}
                    className="w-full bg-yellow-400 hover:bg-yellow-300 text-slate-950 font-black px-4 py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95 disabled:opacity-50 shadow border border-yellow-300/40 font-mono"
                  >
                    {isSearchingCard ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>{t('addCard.quickSearchSearching')}</span>
                      </>
                    ) : (
                      <>
                        <Zap className="w-3.5 h-3.5" />
                        <span>{t('addCard.quickSearchBtn')}</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Feedback Alerts */}
                {searchError && (
                  <p className="text-rose-400 text-[11px] font-mono flex items-center gap-1.5 mt-1 bg-rose-950/40 p-2 rounded-lg border border-rose-900/60">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>{searchError}</span>
                  </p>
                )}

                {searchSuccess && (
                  <p className="text-emerald-400 text-[11px] font-mono flex items-center gap-1.5 mt-1 bg-emerald-950/40 p-2 rounded-lg border border-emerald-900/60 font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    <span>{searchSuccess}</span>
                  </p>
                )}

                {/* Multiple Candidates Selector */}
                {searchCandidates.length > 0 && (
                  <div className="mt-2.5 pt-2 border-t border-slate-800 space-y-1.5">
                    <span className="text-[10px] text-yellow-300 font-mono block">
                      {t('addCard.quickSearchSelectPrompt', { count: searchCandidates.length })}
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-1 no-scrollbar">
                      {searchCandidates.map((c) => (
                        <div
                          key={c.id}
                          onClick={() => applyCardCandidate(c)}
                          className="flex items-center justify-between gap-2 p-2 rounded-xl bg-slate-800/90 hover:bg-slate-700 text-left border border-slate-700 hover:border-yellow-400/60 transition-all cursor-pointer group"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {c.imageUrl && (
                              <img src={c.imageUrl} alt="" className="w-8 h-11 object-cover rounded shrink-0 border border-slate-600 shadow" />
                            )}
                            <div className="min-w-0 flex-1">
                              <span className="font-bold text-white text-[11px] truncate block">{c.namePt || c.nameEn}</span>
                              <span className="text-[9px] text-slate-300 font-mono block">{c.setName} ({c.setCode} #{c.cardNumber})</span>
                              <span className="text-[8px] text-yellow-400/80 font-mono block">{c.rarityName} • {c.category}</span>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => handleAddCandidateDirectly(c, e)}
                            className="bg-yellow-400 hover:bg-yellow-300 text-slate-950 font-bold px-2 py-1 rounded-lg text-[10px] font-mono shrink-0 active:scale-95 transition-all shadow flex items-center gap-1"
                            title="Adicionar carta e limpar busca"
                          >
                            <Plus className="w-3 h-3" />
                            <span>{t('cardDetail.addBtn')}</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1">{t('addCard.nameEn')}</label>
                  <input
                    type="text"
                    required
                    value={nameEn}
                    onChange={(e) => setNameEn(e.target.value)}
                    placeholder="Ex: Charizard ex"
                    className="w-full bg-pokedex-darker border border-slate-800 rounded-xl p-2.5 text-white focus:outline-none focus:border-pokedex-blue text-xs font-sans"
                  />
                </div>

                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1">{t('addCard.namePt')}</label>
                  <input
                    type="text"
                    value={namePt}
                    onChange={(e) => setNamePt(e.target.value)}
                    placeholder={t('addCard.namePtPlaceholder')}
                    className="w-full bg-pokedex-darker border border-slate-800 rounded-xl p-2.5 text-white focus:outline-none focus:border-pokedex-blue text-xs font-sans"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1 h-7 flex items-end leading-tight">{t('addCard.setCode')}</label>
                  <input
                    type="text"
                    required
                    value={setCode}
                    onChange={(e) => setSetCode(e.target.value)}
                    placeholder={t('addCard.setCodePlaceholder')}
                    className="w-full bg-pokedex-darker border border-slate-800 rounded-xl p-2.5 text-white focus:outline-none focus:border-pokedex-blue text-xs uppercase"
                  />
                </div>

                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1 h-7 flex items-end leading-tight">{t('addCard.cardNumber')}</label>
                  <input
                    type="text"
                    required
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value)}
                    placeholder={t('addCard.cardNumberPlaceholder')}
                    className="w-full bg-pokedex-darker border border-slate-800 rounded-xl p-2.5 text-white focus:outline-none focus:border-pokedex-blue text-xs"
                  />
                </div>

                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1 h-7 flex items-end leading-tight">{t('addCard.totalInSet')}</label>
                  <input
                    type="text"
                    value={totalInSet}
                    onChange={(e) => setTotalInSet(e.target.value)}
                    placeholder={t('addCard.totalInSetPlaceholder')}
                    className="w-full bg-pokedex-darker border border-slate-800 rounded-xl p-2.5 text-white focus:outline-none focus:border-pokedex-blue text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1 h-7 flex items-end leading-tight">{t('addCard.typeCategory')}</label>
                  <div className="relative">
                    <select
                      value={colorCode}
                      onChange={(e) => setColorCode(e.target.value)}
                      className="w-full appearance-none bg-pokedex-darker border border-slate-800 rounded-xl pl-3 pr-8 py-2 text-white focus:outline-none focus:border-pokedex-blue text-xs font-sans cursor-pointer"
                    >
                      <option value="R">{t('filters.types.fire')}</option>
                      <option value="W">{t('filters.types.water')}</option>
                      <option value="G">{t('filters.types.grass')}</option>
                      <option value="L">{t('filters.types.lightning')}</option>
                      <option value="P">{t('filters.types.psychic')}</option>
                      <option value="F">{t('filters.types.fighting')}</option>
                      <option value="D">{t('filters.types.darkness')}</option>
                      <option value="M">{t('filters.types.metal')}</option>
                      <option value="Y">{t('filters.types.fairy')}</option>
                      <option value="O">{t('filters.types.dragon')}</option>
                      <option value="C">{t('filters.types.colorless')}</option>
                      <option value="">{t('filters.types.trainer')}</option>
                      <option value="E">{t('filters.types.energy')}</option>
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1 h-7 flex items-end leading-tight">{t('addCard.rarity')}</label>
                  <div className="relative">
                    <select
                      value={rarityCode}
                      onChange={(e) => setRarityCode(e.target.value)}
                      className="w-full appearance-none bg-pokedex-darker border border-slate-800 rounded-xl pl-3 pr-8 py-2 text-white focus:outline-none focus:border-pokedex-blue text-xs font-sans cursor-pointer"
                    >
                      <option value="C">{t('addCard.rarityCommon')}</option>
                      <option value="U">{t('addCard.rarityUncommon')}</option>
                      <option value="R">{t('addCard.rarityRare')}</option>
                      <option value="RH">{t('addCard.rarityRareHolo')}</option>
                      <option value="RU">{t('addCard.rarityUltraRare')}</option>
                      <option value="RD">{t('addCard.rarityDoubleRare')}</option>
                      <option value="IR">{t('addCard.rarityIllustrationRare')}</option>
                      <option value="S">{t('addCard.raritySecretRare')}</option>
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="text-slate-400 block text-[10px] uppercase mb-1 h-7 flex items-end leading-tight">{t('addCard.quantity')}</label>
                  <input
                    type="number"
                    min="0"
                    max="99"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                    className="w-full bg-pokedex-darker border border-slate-800 rounded-xl p-2.5 text-white focus:outline-none focus:border-pokedex-blue text-xs"
                  />
                </div>
              </div>

              {/* Image Upload & Firebase Storage Section */}
              <div className="space-y-3 p-3 bg-pokedex-darker rounded-2xl border border-slate-800">
                <div className="flex items-center justify-between">
                  <label className="text-cyan-300 font-bold block text-[10px] uppercase flex items-center gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5" />
                    <span>{t('addCard.imageSectionTitle')}</span>
                  </label>
                  <span className="text-[10px] text-slate-500 font-mono">Firebase Storage</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                  <div>
                    <label className="text-slate-400 block text-[10px] uppercase mb-1">{t('addCard.imageOptionA')}</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileChange}
                      className="block w-full text-[11px] text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-[11px] file:font-semibold file:bg-slate-800 file:text-yellow-300 hover:file:bg-slate-700 cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="text-slate-400 block text-[10px] uppercase mb-1">{t('addCard.imageOptionB')}</label>
                    <input
                      type="url"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://images.pokemontcg.io/sv1/54.png"
                      className="w-full bg-black/50 border border-slate-700 rounded-xl p-2 text-white focus:outline-none focus:border-pokedex-blue text-xs"
                    />
                  </div>
                </div>

                {imagePreview && (
                  <div className="flex items-center space-x-3 pt-1">
                    <img src={imagePreview} alt="Preview" className="w-12 h-16 rounded-lg object-cover border border-slate-700 shadow-md" />
                    <span className="text-[11px] text-emerald-400">✓ {t('addCard.imageReadyNotice')}</span>
                  </div>
                )}
              </div>

              {/* Foil Checkbox */}
              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  id="foil-check"
                  checked={isFoil}
                  onChange={(e) => setIsFoil(e.target.checked)}
                  className="w-4 h-4 rounded text-pokedex-red focus:ring-0 bg-pokedex-darker border-slate-800"
                />
                <label htmlFor="foil-check" className="text-slate-200 text-xs flex items-center gap-1 cursor-pointer">
                  <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
                  <span>{t('addCard.isFoil')}</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isUploadingImage}
                className="w-full bg-pokedex-red hover:bg-pokedex-lightred disabled:opacity-50 text-white font-bold py-3 rounded-2xl shadow-lg transition-all active:scale-95 text-xs uppercase tracking-wider flex items-center justify-center gap-2"
              >
                {isUploadingImage ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-yellow-300" />
                    <span>{t('addCard.uploadingAndSaving')}</span>
                  </>
                ) : (
                  <span>{t('addCard.saveCardBtn')}</span>
                )}
              </button>
            </form>
            )
          ) : tab === 'csv' ? (
            <div className="space-y-4">
              <p className="text-slate-300 text-xs font-sans">
                {t('addCard.csvHelp')}
              </p>

              <div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="block w-full text-xs text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-pokedex-darker file:text-yellow-300 hover:file:bg-slate-800 cursor-pointer"
                />
              </div>

              <textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder="Set Name (PT),Set Name (EN),Set Code,Card Name (PT),Card Name (EN),Quantity,Quality,Language,Rarity,Color,Extras,Card #,Comment,Total In Set"
                rows={5}
                className="w-full bg-black/60 font-mono text-cyan-300 border border-slate-800 rounded-xl p-3 focus:outline-none focus:border-pokedex-blue text-[11px]"
              />

              {/* Deck Destination Controls */}
              <div className="bg-pokedex-darker p-3.5 rounded-2xl border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-300 uppercase font-bold flex items-center gap-1.5 font-mono">
                    <Layers className="w-3.5 h-3.5 text-yellow-400" />
                    <span>{t('addCard.deckAssignment')}</span>
                  </span>
                  <span className="text-[9px] text-slate-500 font-mono">{t('common.optional')}</span>
                </div>

                {/* Segment Options */}
                <div className="grid grid-cols-3 gap-1.5 bg-black/60 p-1 rounded-xl border border-slate-800 font-mono">
                  <button
                    type="button"
                    onClick={() => setDeckMode('none')}
                    className={`py-2 px-2 rounded-lg text-[10px] font-bold transition-all truncate text-center ${
                      deckMode === 'none'
                        ? 'bg-slate-800 text-yellow-300 shadow border border-slate-700'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {t('addCard.deckModeNone')}
                  </button>
                  <button
                    type="button"
                    disabled={decks.length === 0}
                    onClick={() => setDeckMode('existing')}
                    className={`py-2 px-2 rounded-lg text-[10px] font-bold transition-all truncate text-center disabled:opacity-30 ${
                      deckMode === 'existing'
                        ? 'bg-pokedex-blue text-white shadow border border-blue-400/40'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {t('addCard.deckModeExisting')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeckMode('new')}
                    className={`py-2 px-2 rounded-lg text-[10px] font-bold transition-all truncate text-center ${
                      deckMode === 'new'
                        ? 'bg-emerald-600 text-white shadow border border-emerald-400/40'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {t('addCard.deckModeNew')}
                  </button>
                </div>

                {/* Sub-form: Existing Deck Dropdown */}
                {deckMode === 'existing' && (
                  <div className="space-y-1.5 animate-in fade-in">
                    <label className="text-slate-400 block text-[10px] uppercase font-mono">{t('addCard.selectExistingDeck')}</label>
                    <div className="relative">
                      <select
                        value={targetDeckId}
                        onChange={(e) => setTargetDeckId(e.target.value)}
                        className="w-full appearance-none bg-black/60 border border-slate-700 rounded-xl pl-3 pr-8 py-2 text-xs text-white focus:outline-none focus:border-pokedex-blue font-sans cursor-pointer truncate"
                      >
                        {decks.map(d => (
                          <option key={d.id} value={d.id}>{d.name} ({d.format})</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Sub-form: Create New Deck Fields */}
                {deckMode === 'new' && (
                  <div className="space-y-2.5 animate-in fade-in">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="sm:col-span-2">
                        <label className="text-slate-400 block text-[10px] uppercase mb-1 font-mono">{t('addCard.newDeckName')}</label>
                        <input
                          type="text"
                          value={newDeckName}
                          onChange={(e) => setNewDeckName(e.target.value)}
                          placeholder={t('addCard.newDeckNamePlaceholder')}
                          className="w-full bg-black/60 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-pokedex-blue font-sans"
                        />
                      </div>
                      <div>
                        <label className="text-slate-400 block text-[10px] uppercase mb-1 font-mono">{t('addCard.format')}</label>
                        <div className="relative">
                          <select
                            value={newDeckFormat}
                            onChange={(e) => setNewDeckFormat(e.target.value as any)}
                            className="w-full appearance-none bg-black/60 border border-slate-700 rounded-xl pl-2.5 pr-7 py-2 text-xs text-white focus:outline-none focus:border-pokedex-blue font-sans cursor-pointer"
                          >
                            <option value="Standard">{t('createDeck.formatStandard')}</option>
                            <option value="Expanded">{t('createDeck.formatExpanded')}</option>
                            <option value="Casual">{t('createDeck.formatCasual')}</option>
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {importResult && (
                <div className="bg-emerald-950 border border-emerald-500/40 p-3 rounded-xl text-emerald-300 text-xs flex items-center gap-2 animate-in fade-in">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>
                    {t('addCard.importSuccess', { added: importResult.added, updated: importResult.updated })}
                    {importResult.deckName ? ` (assigned to deck "${importResult.deckName}")` : ''}!
                  </span>
                </div>
              )}

              <button
                onClick={handleCsvImport}
                disabled={!csvText.trim()}
                className="w-full bg-pokedex-blue hover:bg-blue-600 disabled:opacity-40 text-white font-bold py-3 rounded-2xl shadow-lg transition-all active:scale-95 text-xs uppercase tracking-wider flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                <span>
                  {deckMode === 'new' && newDeckName.trim()
                    ? t('addCard.importAndCreateDeck', { name: newDeckName.trim() })
                    : deckMode === 'existing'
                    ? t('addCard.importAndAddToDeck')
                    : t('addCard.processCsv')}
                </span>
              </button>
            </div>
          ) : (
            <DeckValidatorTab />
          )}
        </div>
      </div>
    </div>
  );
};
